/**
 * BeHeard App Role Profiles
 *
 * Maps Central role + assigned scopes to BeHeard-specific display profiles.
 *
 * IMPORTANT: This is a DISPLAY / MAPPING layer only.
 * It does NOT control authorization. All access enforcement uses:
 * - requireCapability() on backend routes
 * - hasCapability() in frontend UI gates
 *
 * Capabilities remain fail-closed. A user may have a profile label
 * but still be denied access if the underlying scope is removed.
 *
 * Resolution rules:
 * - A profile is only assigned if its FULL scope bundle is present.
 * - Partial scope sets produce "member_custom" (honest partial-access label).
 * - Additive profiles are matched independently; each requires its full bundle.
 */

// ============================================================
// Types
// ============================================================

export type CentralRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface AppRoleProfile {
  /** Unique key for this profile */
  key: AppRoleKey;
  /** Human-readable name */
  displayName: string;
  /** Short description for admin/member UX */
  description: string;
  /** Central workspace role required as a prerequisite */
  centralRole: CentralRole | CentralRole[];
  /** Scopes that define this profile. Empty = role-only (owner/viewer). */
  scopes: string[];
  /** If true, this profile is additive (can be combined with other member profiles) */
  additive: boolean;
  /** Badge color for UI display */
  badgeColor: 'purple' | 'blue' | 'green' | 'amber' | 'rose' | 'gray' | 'indigo' | 'teal' | 'slate';
  /** Sort order for display */
  order: number;
}

export type AppRoleKey =
  | 'owner'
  | 'admin'
  | 'member_standard'
  | 'member_premium'
  | 'member_recruiter'
  | 'member_trainer'
  | 'member_org_admin'
  | 'member_custom'
  | 'viewer';

// ============================================================
// Scope Bundles
// ============================================================

/**
 * Recommended scope bundles for each profile.
 * Every scope listed here exists in Central's capabilitiesRegistry for beheard.
 */
export const SCOPE_BUNDLES: Record<string, string[]> = {
  admin: [
    'beheard:admin',
  ],

  member_standard: [
    'beheard:practice:read',
    'beheard:practice:write',
    'beheard:coaching:read',
    'beheard:coaching:write',
    'beheard:marketplace:read',
    'beheard:portfolio:read',
    'beheard:portfolio:write',
    'beheard:billing:read',
  ],

  member_premium: [
    'beheard:practice:ai',
    'beheard:ai:coach',
    'beheard:ai:voice',
    'beheard:ai:tts',
    'beheard:marketplace:listen',
    'beheard:coaching:voice',
  ],

  member_recruiter: [
    'beheard:recruiter:read',
    'beheard:recruiter:write',
  ],

  member_trainer: [
    'beheard:training:read',
    'beheard:training:write',
  ],

  member_org_admin: [
    'beheard:org:read',
    'beheard:org:admin',
  ],
};

// ============================================================
// Profile Definitions
// ============================================================

export const APP_ROLE_PROFILES: AppRoleProfile[] = [
  {
    key: 'owner',
    displayName: 'Owner',
    description: 'Full BeHeard access. Manages workspace, billing, and all features.',
    centralRole: 'owner',
    scopes: [],
    additive: false,
    badgeColor: 'purple',
    order: 0,
  },
  {
    key: 'admin',
    displayName: 'Admin',
    description: 'Full BeHeard admin access. Can manage users, content, and settings.',
    centralRole: 'admin',
    scopes: SCOPE_BUNDLES.admin,
    additive: false,
    badgeColor: 'indigo',
    order: 1,
  },
  {
    key: 'member_standard',
    displayName: 'Member',
    description: 'Core features: practice sessions, coaching, portfolio, and marketplace browsing.',
    centralRole: 'member',
    scopes: SCOPE_BUNDLES.member_standard,
    additive: false,
    badgeColor: 'blue',
    order: 2,
  },
  {
    key: 'member_premium',
    displayName: 'Premium',
    description: 'AI coaching, live voice practice, text-to-speech, and marketplace listening.',
    centralRole: 'member',
    scopes: SCOPE_BUNDLES.member_premium,
    additive: true,
    badgeColor: 'amber',
    order: 3,
  },
  {
    key: 'member_recruiter',
    displayName: 'Recruiter',
    description: 'Candidate discovery, evaluations, hiring pipelines, and contact requests.',
    centralRole: 'member',
    scopes: SCOPE_BUNDLES.member_recruiter,
    additive: true,
    badgeColor: 'rose',
    order: 4,
  },
  {
    key: 'member_trainer',
    displayName: 'Trainer',
    description: 'Create and assign training modules, track learner progress.',
    centralRole: 'member',
    scopes: SCOPE_BUNDLES.member_trainer,
    additive: true,
    badgeColor: 'teal',
    order: 5,
  },
  {
    key: 'member_org_admin',
    displayName: 'Org Manager',
    description: 'Organization member management, analytics, and settings.',
    centralRole: 'member',
    scopes: SCOPE_BUNDLES.member_org_admin,
    additive: true,
    badgeColor: 'green',
    order: 6,
  },
  {
    key: 'member_custom',
    displayName: 'Custom Access',
    description: 'Partial BeHeard access based on individually assigned scopes.',
    centralRole: 'member',
    scopes: [],
    additive: false,
    badgeColor: 'slate',
    order: 8,
  },
  {
    key: 'viewer',
    displayName: 'Viewer',
    description: 'Read-only access. Can view practice sessions, coaching records, and reports.',
    centralRole: 'viewer',
    scopes: [],
    additive: false,
    badgeColor: 'gray',
    order: 9,
  },
];

// ============================================================
// Lookup helpers
// ============================================================

const profilesByKey = new Map(APP_ROLE_PROFILES.map((p) => [p.key, p]));

export function getProfile(key: AppRoleKey): AppRoleProfile | undefined {
  return profilesByKey.get(key);
}

// ============================================================
// Resolution Logic
// ============================================================

export interface ResolvedAppRole {
  /** All active profiles for this user */
  profiles: AppRoleKey[];
  /** Primary profile for display (the highest-precedence non-additive, or first) */
  primaryProfile: AppRoleKey;
  /** Display name for the primary profile */
  primaryDisplayName: string;
  /** All display labels (e.g. ["Member", "Premium", "Recruiter"]) */
  labels: string[];
  /** Short summary line (e.g. "Member + Premium + Recruiter") */
  summary: string;
}

/**
 * Resolve BeHeard app role profiles from Central role + scopes.
 *
 * This is for DISPLAY ONLY — it does NOT grant access.
 * Authorization is always via hasCapability() / requireCapability().
 *
 * Rules:
 * - A profile is assigned ONLY if its full scope bundle is present.
 * - Partial scopes produce "member_custom" — never a false "member_standard".
 * - Additive profiles are matched independently.
 *
 * @param centralRole  The user's workspace role from Central
 * @param scopes       The user's granted scopes (entitlementsEffective)
 */
export function resolveAppRoles(
  centralRole: CentralRole,
  scopes: string[]
): ResolvedAppRole {
  const scopeSet = new Set(scopes);

  // Owner: full access, no scope matching needed
  if (centralRole === 'owner') {
    return result(['owner']);
  }

  // Viewer: read-only, no additive profiles
  if (centralRole === 'viewer') {
    return result(['viewer']);
  }

  // Admin: check for beheard:admin scope
  if (centralRole === 'admin' && scopeSet.has('beheard:admin')) {
    return result(['admin']);
  }

  // Member (or admin without beheard:admin): strict full-bundle matching only
  const hasFullBundle = (bundleKey: string): boolean => {
    const bundle = SCOPE_BUNDLES[bundleKey];
    if (!bundle) return false;
    return bundle.every((s) => scopeSet.has(s));
  };

  const active: AppRoleKey[] = [];

  // Check base member profile — FULL bundle required
  if (hasFullBundle('member_standard')) {
    active.push('member_standard');
  }

  // Check each additive profile — each requires its own FULL bundle
  if (hasFullBundle('member_premium')) active.push('member_premium');
  if (hasFullBundle('member_recruiter')) active.push('member_recruiter');
  if (hasFullBundle('member_trainer')) active.push('member_trainer');
  if (hasFullBundle('member_org_admin')) active.push('member_org_admin');

  // No full bundles matched
  if (active.length === 0) {
    const hasAnyBeheardScope = scopes.some((s) => s.startsWith('beheard:'));
    if (hasAnyBeheardScope) {
      // Partial scopes — honest "Custom Access" label
      return result(['member_custom']);
    }
    // No beheard scopes at all
    return {
      profiles: [],
      primaryProfile: 'viewer',
      primaryDisplayName: 'Viewer',
      labels: [],
      summary: `${capitalize(centralRole)} — no BeHeard scopes assigned`,
    };
  }

  return result(active);
}

/**
 * Build a ResolvedAppRole from a sorted list of active profile keys.
 */
function result(active: AppRoleKey[]): ResolvedAppRole {
  const profiles = active.sort(
    (a, b) => (profilesByKey.get(a)?.order ?? 99) - (profilesByKey.get(b)?.order ?? 99)
  );
  const primary = profiles[0];
  const labels = profiles.map((k) => profilesByKey.get(k)?.displayName ?? k);

  return {
    profiles,
    primaryProfile: primary,
    primaryDisplayName: profilesByKey.get(primary)?.displayName ?? primary,
    labels,
    summary: buildSummary(primary, labels),
  };
}

function buildSummary(primary: AppRoleKey, labels: string[]): string {
  const profile = profilesByKey.get(primary);
  if (!profile) return labels.join(' + ');

  // Special summaries for single-profile cases
  if (labels.length === 1) {
    switch (primary) {
      case 'owner': return 'Owner — full access';
      case 'admin': return 'Admin — full BeHeard access';
      case 'viewer': return 'Viewer — read-only';
      case 'member_custom': return 'Custom Access — partial BeHeard scopes';
      default: return profile.displayName;
    }
  }

  return labels.join(' + ');
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
